// Copyright 2026, Auxilio Technologies LLC

import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Add language header
    const language = localStorage.getItem('i18nextLng') || 'en';
    config.headers['Accept-Language'] = language;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { refreshToken, updateTokens, logout } = useAuthStore.getState();

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;
          updateTokens(access_token, refresh_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          logout();
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      } else {
        logout();
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// API namespaces
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (data: { email: string; username: string; password: string; role?: string; invitation_token?: string }) =>
    api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refresh_token: refreshToken }),
  me: () => api.get('/auth/me'),
  verifyEmail: (email: string, code: string) =>
    api.post('/auth/verify-email', { email, code }),
  resendVerificationEmail: (email: string) =>
    api.post('/auth/resend-verification-email', { email }),
  // User management
  listUsers: () => api.get('/auth/users'),
  updateUser: (userId: string, data: { role?: string; division?: string; site_id?: string; is_active?: boolean }) =>
    api.patch(`/auth/users/${userId}`, data),
  deleteUser: (userId: string) => api.delete(`/auth/users/${userId}`),
  inviteUser: (data: { email: string; role: string; division?: string | null; site_id?: string | null }) =>
    api.post('/auth/invite', data),
};

export const shipmentsAPI = {
  list: (params?: Record<string, any>) => api.get('/shipments', { params }),
  get: (id: string) => api.get(`/shipments/${id}`),
  create: (data: Record<string, any>) => api.post('/shipments', data),
  update: (id: string, data: Record<string, any>) => api.put(`/shipments/${id}`, data),
  delete: (id: string) => api.delete(`/shipments/${id}`),
  summary: (params?: Record<string, any>) => api.get('/shipments/summary', { params }),
  import: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/shipments/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const deliveriesAPI = {
  schedule: (params: Record<string, any>) => api.get('/deliveries/schedule', { params }),
  today: (params?: Record<string, any>) => api.get('/deliveries/today', { params }),
  collections: (params: Record<string, any>) => api.get('/deliveries/collections', { params }),
  updateStatus: (id: string, status: string, data?: Record<string, any>) =>
    api.put(`/deliveries/${id}/status`, { status, ...data }),
  performance: (params: Record<string, any>) => api.get('/deliveries/performance', { params }),
};

export const routesAPI = {
  list: (params?: Record<string, any>) => api.get('/routes/', { params }),
  get: (id: string) => api.get(`/routes/${id}`),
  create: (data: Record<string, any>) => api.post('/routes', data),
  update: (id: string, data: Record<string, any>) => api.put(`/routes/${id}`, data),
  updateStatus: (id: string, status: string, data?: Record<string, any>) =>
    api.put(`/routes/${id}/status`, { status, ...data }),
  optimize: (data: Record<string, any>) => api.post('/routes/optimize', data),
  consolidationSuggestions: (siteId: string, date: string) =>
    api.post('/routes/consolidation-suggestions', { site_id: siteId, date }),
  whatIf: (data: Record<string, any>) => api.post('/routes/what-if', data),
};

export const forecastingAPI = {
  list: (params: Record<string, any>) => api.get('/forecasting', { params }),
  items: (params?: Record<string, any>) => api.get('/forecasting/items', { params }),
  getItem: (id: string) => api.get(`/forecasting/items/${id}`),
  generate: (data: Record<string, any>) => api.post('/forecasting/generate', data),
  capacityRisks: (params?: Record<string, any>) => api.get('/forecasting/capacity-risks', { params }),
  accuracy: (params: Record<string, any>) => api.get('/forecasting/accuracy', { params }),
  seasonality: (params?: Record<string, any>) => api.get('/forecasting/seasonality', { params }),
  vsCapacity: (params?: Record<string, any>) => api.get('/forecasting/vs-capacity', { params }),
  aiInsights: (forecasts: Record<string, any>[]) => api.post('/forecasting/ai-insights', { forecasts }),
};

export const suppliersAPI = {
  list: (params?: Record<string, any>) => api.get('/suppliers', { params }),
  get: (id: string) => api.get(`/suppliers/${id}`),
  create: (data: Record<string, any>) => api.post('/suppliers', data),
  update: (id: string, data: Record<string, any>) => api.put(`/suppliers/${id}`, data),
  scorecard: (id: string, params: Record<string, any>) =>
    api.get(`/suppliers/${id}/scorecard`, { params }),
  allScorecards: (params: Record<string, any>) =>
    api.get('/suppliers/scorecards/all', { params }),
  performanceTrend: (id: string, months?: number) =>
    api.get(`/suppliers/${id}/performance/trend`, { params: { months } }),
  issues: (id: string, params: Record<string, any>) =>
    api.get(`/suppliers/${id}/issues`, { params }),
  benchmark: (params: Record<string, any>) => api.get('/suppliers/benchmark', { params }),
};

export const sitesAPI = {
  list: (params?: Record<string, any>) => api.get('/sites/', { params }),
  get: (id: string) => api.get(`/sites/${id}`),
  create: (data: Record<string, any>) => api.post('/sites/', data),
  update: (id: string, data: Record<string, any>) => api.put(`/sites/${id}`, data),
  delete: (id: string) => api.delete(`/sites/${id}`),
  divisions: () => api.get('/sites/divisions'),
  regions: () => api.get('/sites/regions'),
  summary: (id: string) => api.get(`/sites/${id}/summary`),
  capacity: (id: string) => api.get(`/sites/${id}/capacity`),
  performance: (id: string, days?: number) =>
    api.get(`/sites/${id}/performance`, { params: { days } }),
  mapData: () => api.get('/sites/map/data'),
};

export const controlTowerAPI = {
  status: () => api.get('/control-tower/status'),
  liveView: (params?: Record<string, any>) => api.get('/control-tower/live-view', { params }),
  truckUtilization: (params: Record<string, any>) =>
    api.get('/control-tower/truck-utilization', { params }),
  assetInventory: (params?: Record<string, any>) =>
    api.get('/control-tower/asset-inventory', { params }),
  alertsSummary: () => api.get('/control-tower/alerts/summary'),
  exceptions: (params?: Record<string, any>) => api.get('/control-tower/exceptions', { params }),
  kpis: (params: Record<string, any>) => api.get('/control-tower/kpis', { params }),
  mapData: () => api.get('/control-tower/map'),
};

export const analyticsAPI = {
  summary: (params: Record<string, any>) => api.get('/analytics/summary', { params }),
  byPeriod: (period: string) => api.get(`/analytics/period/${period}`),
  costs: (params: Record<string, any>) => api.get('/analytics/costs', { params }),
  waste: (params: Record<string, any>) => api.get('/analytics/waste', { params }),
  optimizationImpact: (params: Record<string, any>) =>
    api.get('/analytics/optimization-impact', { params }),
  dotCot: (params: Record<string, any>) => api.get('/analytics/performance/dot-cot', { params }),
  capacityUtilization: (params: Record<string, any>) =>
    api.get('/analytics/capacity-utilization', { params }),
  executiveDashboard: () => api.get('/analytics/dashboard/executive'),
  operationsDashboard: () => api.get('/analytics/dashboard/operations'),
};

export const alertsAPI = {
  list: (params?: Record<string, any>) => api.get('/alerts/', { params }),
  active: () => api.get('/alerts/active'),
  summary: (params: Record<string, any>) => api.get('/alerts/summary', { params }),
  get: (id: string) => api.get(`/alerts/${id}`),
  acknowledge: (id: string, notes?: string) =>
    api.put(`/alerts/${id}/acknowledge`, { notes }),
  resolve: (id: string, notes: string) =>
    api.put(`/alerts/${id}/resolve?resolution_notes=${encodeURIComponent(notes)}`),
  bulkAcknowledge: (ids: string[], notes?: string) =>
    api.post('/alerts/bulk-acknowledge', { alert_ids: ids, notes }),
  rules: () => api.get('/alerts/rules'),
  notificationSettings: () => api.get('/alerts/notifications/settings'),
  updateNotificationSettings: (data: Record<string, any>) =>
    api.put('/alerts/notifications/settings', data),
  // Notification Rules CRUD
  notificationRules: () => api.get('/alerts/notification-rules'),
  getNotificationRule: (id: string) => api.get(`/alerts/notification-rules/${id}`),
  createNotificationRule: (data: Record<string, any>) =>
    api.post('/alerts/notification-rules', data),
  updateNotificationRule: (id: string, data: Record<string, any>) =>
    api.put(`/alerts/notification-rules/${id}`, data),
  deleteNotificationRule: (id: string) =>
    api.delete(`/alerts/notification-rules/${id}`),
};

export const exportAPI = {
  shipments: (data: Record<string, any>) => api.post('/export/shipments', data),
  supplierScorecard: (supplierId: string, data: Record<string, any>) =>
    api.post(`/export/supplier-scorecard/${supplierId}`, data),
  allScorecards: (data: Record<string, any>) =>
    api.post('/export/supplier-scorecards/all', data),
  costAnalysis: (data: Record<string, any>) => api.post('/export/cost-analysis', data),
  forecast: (data: Record<string, any>) => api.post('/export/forecast', data),
  executiveSummary: (data: Record<string, any>) =>
    api.post('/export/executive-summary', data),
  dotCotReport: (data: Record<string, any>) => api.post('/export/dot-cot-report', data),
  status: (exportId: string) => api.get(`/export/status/${exportId}`),
  download: (exportId: string) =>
    api.get(`/export/download/${exportId}`, { responseType: 'blob' }),
  templates: () => api.get('/export/templates'),
};

export const feedbackAPI = {
  submit: (data: { feedback_type?: string; feature_area?: string; comment: string }) =>
    api.post('/feedback/submit', data),
  getTypes: () => api.get('/feedback/types'),
};

export default api;
